import { isDoggosAdmin } from './doggosRoles'

const STORAGE_KEY = 'doggos_dashboard_diagnostics_v32'
const DIAGNOSTICS_CACHE_VERSION = 37

let cacheGeneration = 0
let cachedPayload = null
let cachedError = null
let inflightFull = null
let inflightUpcoming = null
let staleRefreshAttempted = false
const listeners = new Set()

function notifyListeners() {
  listeners.forEach((listener) => listener())
}

function getAuthToken() {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem('doggos_l')
}

function decodeJwtPayload(token) {
  if (!token || typeof window === 'undefined') return null
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return JSON.parse(window.atob(padded))
  } catch {
    return null
  }
}

function decodeJwtEmail(token) {
  return decodeJwtPayload(token)?.email || null
}

function readStoredDiagnostics() {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.generatedAt ? parsed : null
  } catch {
    return null
  }
}

function writeStoredDiagnostics(payload) {
  if (typeof window === 'undefined' || !payload) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // ignore quota / privacy mode
  }
}

function clearStoredDiagnostics() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

function hydrateFromStorage() {
  if (cachedPayload) return cachedPayload
  const stored = readStoredDiagnostics()
  if (stored) {
    cachedPayload = stored
    cachedError = null
  }
  return stored
}

function mergeDiagnosticsPayload(existing, incoming) {
  if (!incoming) return existing || null
  if (!existing) return incoming

  if (incoming.slice === 'upcoming-crons') {
    return {
      ...existing,
      generatedAt: incoming.generatedAt || existing.generatedAt,
      diagnosticsCacheVersion: incoming.diagnosticsCacheVersion,
      slice: existing.payments ? undefined : 'upcoming-crons',
      cron: {
        ...(existing.cron || {}),
        ...(incoming.cron || {}),
      },
      balanceTargets: incoming.balanceTargets || existing.balanceTargets,
    }
  }

  return {
    ...existing,
    ...incoming,
    cron: {
      ...(existing.cron || {}),
      ...(incoming.cron || {}),
    },
    slice: undefined,
  }
}

export function isDiagnosticsCacheStale(data) {
  if (!data) return false
  if (data.diagnosticsCacheVersion !== DIAGNOSTICS_CACHE_VERSION) return true
  // Payments live on the full diagnostics payload — upcoming-only cache is incomplete.
  if (!data.payments || !data.payments.codes) return true
  if (typeof data.payments.lookbackDays !== 'number') return true
  if (data?.cron && typeof data.cron.executionsToday !== 'number') return true
  if (data?.cron && typeof data.cron.executionsLast30Days !== 'number') {
    return true
  }
  if (data?.cron?.missed > 0 && !Array.isArray(data?.cron?.pendingChargeCodes)) {
    return true
  }
  if (data?.cron && typeof data.cron.queued !== 'number') return true
  if (
    data?.cron?.queued > 0 &&
    typeof data.cron.upcomingReservationCount !== 'number'
  ) {
    return true
  }
  if (data?.cron?.queued > 0 && !Array.isArray(data.cron.queuedJobs)) return true
  if (
    data?.cron?.queued > 0 &&
    Array.isArray(data.cron.queuedJobs) &&
    data.cron.queuedJobs.some(
      (job) =>
        !Array.isArray(job?.reservationCodes) ||
        !Array.isArray(job?.reservations),
    )
  ) {
    return true
  }
  if (
    (data?.cron?.executionsLast30Days > 0 || data?.cron?.ran > 0) &&
    !Array.isArray(data.cron.ranJobs)
  ) {
    return true
  }
  if (
    (data?.cron?.executionsLast30Days > 0 || data?.cron?.ran > 0) &&
    Array.isArray(data.cron.ranJobs) &&
    data.cron.ranJobs.some(
      (job) =>
        !Array.isArray(job?.reservationCodes) ||
        !Array.isArray(job?.reservations) ||
        !Array.isArray(job?.executions),
    )
  ) {
    return true
  }
  if (data?.cron && !Array.isArray(data.cron.scheduledRunTimes)) return true
  if (
    !data?.balanceTargets ||
    typeof data.balanceTargets.dueTodayUnpaid !== 'number'
  ) {
    return true
  }
  return false
}

export function refreshStaleDiagnosticsCacheIfNeeded(data) {
  if (!data || staleRefreshAttempted || !isDiagnosticsCacheStale(data)) {
    return
  }
  staleRefreshAttempted = true
  prefetchDashboardDiagnostics({ force: true }).catch(() => {})
}

/**
 * @param {string | { email?: string, doggosRole?: string } | null | undefined} profileOrEmail
 */
export function shouldPrefetchDashboardDiagnostics(profileOrEmail) {
  if (!profileOrEmail) return false
  if (typeof profileOrEmail === 'string') {
    return isDoggosAdmin({ email: profileOrEmail })
  }
  return isDoggosAdmin(profileOrEmail)
}

export function getDiagnosticsViewerProfile(profile) {
  if (profile?.email || profile?.doggosRole) return profile
  return decodeJwtPayload(getAuthToken())
}

/** @deprecated Prefer getDiagnosticsViewerProfile */
export function getDiagnosticsViewerEmail(profile) {
  if (profile?.email) return profile.email
  return decodeJwtEmail(getAuthToken())
}

export function getDashboardDiagnosticsSnapshot() {
  hydrateFromStorage()
  return {
    data: cachedPayload,
    error: cachedError,
  }
}

export function subscribeDashboardDiagnostics(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function applyDiagnosticsFailure(cause, { keepCached = false } = {}) {
  if (!keepCached || !cachedPayload) {
    cachedError = cause
  }
  notifyListeners()
}

function applyDiagnosticsSuccess(payload) {
  cachedPayload = payload
  cachedError = null
  staleRefreshAttempted = false
  writeStoredDiagnostics(payload)
  notifyListeners()
}

function applyDiagnosticsPartial(payload) {
  cachedPayload = mergeDiagnosticsPayload(cachedPayload, payload)
  cachedError = null
  writeStoredDiagnostics(cachedPayload)
  notifyListeners()
}

export function prefetchDashboardDiagnostics({ force = false } = {}) {
  if (typeof window === 'undefined') return Promise.resolve(null)

  hydrateFromStorage()

  const token = getAuthToken()
  const payload = decodeJwtPayload(token)
  if (!shouldPrefetchDashboardDiagnostics(payload)) {
    return Promise.resolve(null)
  }

  const generation = cacheGeneration
  const hasCached = Boolean(cachedPayload)
  const hasLiteCached =
    hasCached &&
    cachedPayload?.cron &&
    cachedPayload?.balanceTargets &&
    !isDiagnosticsCacheStale(cachedPayload)

  if (!force && hasLiteCached) {
    return Promise.resolve(cachedPayload)
  }

  if (force) {
    if (inflightFull) return inflightFull

    inflightFull = fetchDiagnostics(token)
      .then((payload) => {
        if (generation !== cacheGeneration) return null
        applyDiagnosticsSuccess(mergeDiagnosticsPayload(cachedPayload, payload))
        return cachedPayload
      })
      .catch((cause) => {
        if (generation !== cacheGeneration) return null
        applyDiagnosticsFailure(cause, { keepCached: hasCached })
        return hasCached ? cachedPayload : Promise.reject(cause)
      })
      .finally(() => {
        if (generation === cacheGeneration) inflightFull = null
      })

    return inflightFull
  }

  if (inflightUpcoming) {
    return inflightUpcoming
  }

  inflightUpcoming = fetchDiagnosticsUpcoming(token)
    .then((payload) => {
      if (generation !== cacheGeneration) return null
      applyDiagnosticsPartial(payload)
      // Upcoming slice has no payments — always follow with full diagnostics.
      if (!cachedPayload?.payments || isDiagnosticsCacheStale(cachedPayload)) {
        prefetchDashboardDiagnostics({ force: true }).catch(() => {})
      }
      return cachedPayload
    })
    .catch((cause) => {
      if (generation !== cacheGeneration) return null
      applyDiagnosticsFailure(cause, { keepCached: hasCached })
      // Still try the full payload (includes payments) if upcoming fails.
      prefetchDashboardDiagnostics({ force: true }).catch(() => {})
      return hasCached ? cachedPayload : Promise.reject(cause)
    })
    .finally(() => {
      if (generation === cacheGeneration) inflightUpcoming = null
    })

  return inflightUpcoming
}

/**
 * After a manual/scheduled charge run is recorded, refresh cron diagnostics
 * immediately (upcoming slice first for speed), then full payload in background.
 */
export async function refreshDashboardDiagnosticsAfterCronRun() {
  if (typeof window === 'undefined') return null

  const token = getAuthToken()
  const payload = decodeJwtPayload(token)
  if (!shouldPrefetchDashboardDiagnostics(payload)) {
    return null
  }

  // Drop in-flight fetches so we don't apply a response started before this run.
  const generation = ++cacheGeneration
  inflightFull = null
  inflightUpcoming = null
  staleRefreshAttempted = false

  try {
    const upcoming = await fetchDiagnosticsUpcoming(token)
    if (generation !== cacheGeneration) return null
    applyDiagnosticsPartial(upcoming)
  } catch (cause) {
    if (generation !== cacheGeneration) return null
    applyDiagnosticsFailure(cause, { keepCached: Boolean(cachedPayload) })
  }

  // Full diagnostics in the background (payments, fees, etc.).
  prefetchDashboardDiagnostics({ force: true }).catch(() => {})
  return cachedPayload
}

async function fetchDiagnostics(token) {
  const response = await fetch('/api/diagnostics', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  let body = null
  try {
    body = await response.json()
  } catch {
    body = null
  }

  if (!response.ok) {
    throw new Error(body?.error || 'Could not load diagnostics.')
  }

  if (!body?.generatedAt) {
    throw new Error('Could not load diagnostics.')
  }

  return body
}

async function fetchDiagnosticsUpcoming(token) {
  const response = await fetch('/api/diagnostics-upcoming', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  let body = null
  try {
    body = await response.json()
  } catch {
    body = null
  }

  if (!response.ok) {
    throw new Error(body?.error || 'Could not load upcoming crons.')
  }

  if (!body?.generatedAt || !body?.cron || !body?.balanceTargets) {
    throw new Error('Could not load upcoming crons.')
  }

  return body
}

export function clearDashboardDiagnosticsCache() {
  cacheGeneration++
  cachedPayload = null
  cachedError = null
  inflightFull = null
  inflightUpcoming = null
  staleRefreshAttempted = false
  clearStoredDiagnostics()
  notifyListeners()
}

export function bootstrapDashboardDiagnosticsPrefetch() {
  if (typeof window === 'undefined') return
  hydrateFromStorage()
}

export function ensureDashboardDiagnosticsPrefetch() {
  bootstrapDashboardDiagnosticsPrefetch()
}
